import type PresignChunk from '@/types/PresignChunk';
import { WorkerPool } from '../services/worker';
import type { ChunkType, CompletedChunkType, PreSignedURLResponse } from '../types/Media';
// asd
async function generateSignedUrl(file, totalChunks) {
  const { data: signerResponse, error: signerError, execute } = useSubmit<{ data: PreSignedURLResponse }>();

  await execute('/s3-upload-signed-url/generate', {
    method: 'POST',
    body: {
      file_name: file.name,
      mime_type: file.type,
      size: file.size,
      parts: totalChunks,
      multipart: totalChunks > 1,
    },
  });

  if (signerError.value) {
    return { error: signerError.value };
  }

  return {
    multipart: Array.isArray(signerResponse.value.data?.urls),
    mediaId: signerResponse.value.data?.id,
    uploadId: signerResponse.value.data?.upload_id,
    urls: signerResponse.value.data?.urls,
    url: signerResponse.value.data?.url, // For non-multipart uploads
  };
}

async function uploadChunk(signedUrl, file, chunkSize, totalChunks, retryChunk) {
  const { error: completedError, execute: executeCompleted } = useSubmit();

  return new Promise(async (resolve, reject) => {
    const chunks = [];
    const completedChunks: CompletedChunkType[] = [];
    let completedChunkCount = 0;

    const pool = new WorkerPool(10);

    async function handleResult(result) {
      completedChunkCount++;
      completedChunks.push(result);

      if (completedChunkCount === totalChunks) {
        completedChunks.sort((a, b) => a.part - b.part);

        const handleReject = () => {
          reject({
            multipart: signedUrl.multipart,
            mediaId: signedUrl.mediaId,
            uploadId: signedUrl.uploadId,
            chunks: completedChunks,
            urls: signedUrl.urls,
          });
        };

        if (!completedChunks.filter((a) => !a.etag).length) {
          await executeCompleted('/s3-upload-signed-url/complete', {
            method: 'POST',
            body: {
              media_id: signedUrl.mediaId,
              upload_id: signedUrl.uploadId,
              parts: completedChunks.map((chunk) => ({
                PartNumber: chunk.part,
                ETag: chunk.etag,
              })),
            },
          });

          if (!completedError.value) {
            resolve(signedUrl.mediaId);
          } else {
            handleReject();
          }
        } else {
          handleReject();
        }
      }
    }

    let cursor = 0;

    while (cursor < file.size) {
      const chunkEnd = Math.min(cursor + chunkSize, file.size);
      const chunkIndex = chunks.length; // s3 multipart part numbers start from 1

      const hasExistingChunk =
        retryChunk && retryChunk.chunks && retryChunk.chunks[chunkIndex] && retryChunk.chunks[chunkIndex].etag;

      const chunk: ChunkType = hasExistingChunk
        ? retryChunk.chunks[chunkIndex]
        : {
            start: cursor,
            end: chunkEnd,
            blob: file,
            uploadId: signedUrl.upload_id,
            part: chunkIndex + 1, // s3 multipart part numbers start from 1
            url: signedUrl.urls[chunkIndex],
          };

      chunks.push(chunk);

      if (!hasExistingChunk) {
        pool.queueJob('/workers/chunk-uploader.js', chunk, async (result: CompletedChunkType) => {
          await handleResult(result);
        });
      } else {
        await handleResult(chunk);
      }

      cursor = chunkEnd;
    }
  });
}

export const useMediaUploader = async (file: File, retryChunk: PresignChunk = null) => {
  const fileSize = file.size;
  const chunkSize = 1000 * 1000 * 10;
  const totalChunks = Math.ceil(fileSize / chunkSize);

  const signedUrlResponse = retryChunk ? retryChunk : await generateSignedUrl(file, totalChunks);

  if (signedUrlResponse.multipart) {
    return await uploadChunk(signedUrlResponse, file, chunkSize, totalChunks, retryChunk);
  } else {
    await useFetch(signedUrlResponse.url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
      server: false,
    });

    return signedUrlResponse.mediaId;
  }
};
